import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getSpecialById, specials } from "@/mocks/specials";
import { getMovieById } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";

export default function SpecialPage() {
  const { id = "" } = useParams();
  const special = getSpecialById(id);

  useSeo({
    title: special
      ? `${special.title} - 影评专题 - 欧美大片影视网`
      : "影评专题未找到 - 欧美大片影视网",
    description: special
      ? `${special.subtitle}。${special.intro.slice(0, 60)}…`
      : "你访问的影评专题不存在，返回首页浏览更多欧美电影资讯。",
    keywords: special
      ? `${special.title},影评专题,欧美电影,深度影评`
      : "影评专题,欧美电影",
  });

  if (!special) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 lg:px-12 py-20 md:py-28 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-bookmark-3-line text-3xl" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl text-foreground-900 mb-3">
            专题未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            该影评专题可能已下线，去看看其他专题吧。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-5-line" /> 返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const relatedSpecials = special.related
    .map((rid) => specials.find((s) => s.id === rid))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  return (
    <SiteLayout>
      {/* hero */}
      <section className="relative w-full">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={special.cover}
            alt={`${special.title} 专题主图`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/50" />
        </div>
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影评专题" },
              { label: special.title },
            ]}
          />
          <div className="mt-6 max-w-3xl text-background-50">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-label font-bold mb-4">
              <i className="ri-bookmark-3-fill" /> 影评专题
            </span>
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl leading-tight mb-3">
              {special.title}
            </h1>
            <p className="font-label text-sm tracking-wide text-accent-400 uppercase mb-5">
              {special.subtitle}
            </p>
            <p className="text-sm md:text-base text-background-200/90 leading-loose">
              {special.intro}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          {/* article list */}
          <div className="lg:col-span-8">
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-5">
              专题影评文章
            </h2>
            <div className="space-y-4">
              {special.articles.map((article) => {
                const movie = getMovieById(article.movieId);
                return (
                  <article
                    key={article.id}
                    className="group rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors overflow-hidden"
                  >
                    <Link
                      to={`/detail/${article.movieId}`}
                      className="flex flex-col sm:flex-row gap-4 p-4 md:p-5 cursor-pointer"
                    >
                      {movie && (
                        <div className="w-full sm:w-24 md:w-28 shrink-0 aspect-[2/3] sm:aspect-[2/3] rounded-md overflow-hidden bg-background-200">
                          <img
                            src={movie.poster}
                            alt={`${movie.title} 海报`}
                            className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-bold text-base md:text-lg text-foreground-900 group-hover:text-primary-600 transition-colors mb-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-user-3-line" />
                            {article.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line" />
                            {article.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line" />
                            {article.readCount.toLocaleString()} 阅读
                          </span>
                          {movie && (
                            <span className="flex items-center gap-1 text-accent-700">
                              <i className="ri-film-line" />
                              关联：{movie.title}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>

          {/* sidebar */}
          <aside className="lg:col-span-4">
            <div className="rounded-lg bg-accent-50 border border-accent-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-base text-accent-900 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line" /> 推荐理由
              </h2>
              <p className="text-sm text-foreground-600 leading-loose">
                {special.reason}
              </p>
            </div>

            <div className="mt-5 rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-base text-foreground-900 mb-4">
                相关专题
              </h2>
              <div className="flex flex-col gap-3">
                {relatedSpecials.map((rs) => (
                  <Link
                    key={rs.id}
                    to={`/special/${rs.id}`}
                    className="group flex items-center gap-3 p-3 rounded-md bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                      <i className="ri-bookmark-3-fill" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-medium text-sm text-foreground-800 group-hover:text-primary-600 transition-colors truncate">
                        {rs.title}
                      </span>
                      <span className="block text-xs text-foreground-400 truncate">
                        {rs.subtitle}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}